import { noop } from 'functions/noop';
// eslint-disable-next-line import/no-cycle
import { hasGenderQuestion, isEmptyObject } from 'new-ui/functions';
import { DISEASE as CancerType } from 'utils/constants/disease';
import { QUESTION_ID } from 'modules/questionnaire/questionIds';
import { QUESTION_TYPE } from 'new-ui/Components/QuestionRenderer/constants';

const {
  treatments: {
    questionId: TREATMENTS_QUESTION_ID,
  },
  procedures: {
    questionId: PROCEDURES_QUESTION_ID,
  },
  biomarkers: BIOMARKERS_QUESTION_ID,
  ecog: ECOG_QUESTION_ID,
  initial_diagnosis_date: INITIAL_DIAGNOSIS_DATE_QUESTION_ID,
  other_medical_condition: OTHER_MEDICAL_CONDITION_QUESTION_ID,
  ngs: NGS_QUESTION_ID,
} = QUESTION_ID;

export const INTAKE_SECTIONS = {
  ABOUT_YOUR_CANCER: 'cancer',
  ABOUT_YOUR_TREATMENT: 'treatment',
  ABOUT_YOUR_HEALTH: 'health',
  ABOUT_YOU: 'about',
};

export const INTAKE_QUESTIONS = {
  [INTAKE_SECTIONS.ABOUT_YOUR_CANCER]: {
    [CancerType.BREAST_2]: ['disease_status', 'molecular_subtype', 'her2_levels', 'patient_stage'],
    [CancerType.LUNG]: ['disease_status', 'type_of_lung_cancer', 'type_of_nsclc', 'current_stage', 'has_biopsy'],
    [CancerType.CRC_2]: ['disease_status', 'current_stage'],
    [CancerType.PROSTATE]: ['disease_status', 'crpc', 'current_stage', 'gleason_score', 'psa_level'],
    [CancerType.MELANOMA_2]: ['disease_status', 'melanoma_subtype', 'current_stage'],
    [CancerType.BLADDER_2]: ['disease_status', 'patient_phase', 'patient_grade'],
    [CancerType.RCC]: ['disease_status', 'type_of_rcc', 'patient_stage'],
    [CancerType.GI_TRACT]: ['disease_status', 'type_of_gi', 'type_of_esophageal_gej', 'current_stage'],
    [CancerType.MESO]: ['disease_status', 'type_of_mesothelioma_cancer', 'histological_type_of_mesothelioma', 'patient_stage', 'disease_location'],
    [CancerType.GENERAL]: ['disease_status', 'type_of_pancreatic', 'patient_stage'],
    [CancerType.CML]: ['disease_status', 'cml_phase'],
    [CancerType.OVARIAN]: ['disease_status', 'type_of_ovarian_cancer', 'current_stage', 'had_procedure_for_ovarian_cancer', 'procedures'],
    [CancerType.UTERINE]: ['disease_status', 'type_of_uterine_cancer', 'current_stage'],
    [CancerType.HNSCC]: ['disease_status', 'type_of_hnscc', 'current_stage'],
    [CancerType.BRAIN]: ['disease_status', 'brain_cancer_type', 'brain_radiation'],
    [CancerType.MDS]: ['disease_status', 'risk_group', 'intermediate_risk_subgroup', 'percentage_of_bm_blasts'],
    [CancerType.MPN]: ['disease_status', 'type_of_mpn', 'risk_group', 'intermediate_risk_subgroup', 'splenomegaly', 'platelet_count'],
    [CancerType.MM]: ['disease_status', 'has_mprotein', 'has_serum_flc', 'chromosomal_alterions'],
    [CancerType.NHL]: ['disease_status', 'type_of_lymphoma', 'type_of_bcell_lymphoma', 'type_of_marginal_cell_lymphoma', 'type_of_aids_related_lymphoma', 'current_stage', 'disease_location', 'current_stage_follicular_lymphoma'],
    [CancerType.CLL]: ['disease_status', 'type_of_disease', 'cll_characteristics', 'platelet_count', 'lymphocyte_count'],
    [CancerType.AML]: ['disease_status', 'risk_group', 'received_transplant'],
    [CancerType.HCC]: ['disease_status', 'type_of_liver', 'current_stage', 'number_lesions', 'hcc_related_health_problems'],
    [CancerType.SARCOMA]: ['disease_status', 'sarcoma_type', 'type_of_liposarcoma', 'type_of_kaposi_sarcoma', 'type_of_rhabdomyosarcoma', 'type_of_leiomyosarcoma', 'type_of_chondrosarcoma', 'patient_stage', 'bone_sarcoma_stage', 'sarcoma_grade'],
    [CancerType.CERVICAL]: ['disease_status', 'type_of_cervical_cancer', 'hpv_status', 'hpv_type', 'neuroendocrine_type', 'hpv_independent_adenocarcinoma', 'patient_stage', 'lvi'],
  },
  [INTAKE_SECTIONS.ABOUT_YOUR_TREATMENT]: {
    [CancerType.BREAST_2]: ['treatment_for_advanced_metastatic_disease', 'treatment_for_breast_cancer', 'received_fam_trastuzumab', 'treatments'],
    [CancerType.LUNG]: ['treatment_for_advanced_metastatic_disease', 'treatments'],
    [CancerType.CRC_2]: ['treatment_for_advanced_metastatic_disease', 'treatment_for_crc_cancer', 'treatments'],
    [CancerType.PROSTATE]: ['had_procedure_for_prostate_cancer', 'procedures', 'treatment_for_prostate_cancer', 'treatment_for_advanced_metastatic_disease', 'treatments'],
    [CancerType.MELANOMA_2]: ['treatment_for_advanced_metastatic_disease', 'treatment_for_melanoma', 'treatments'],
    [CancerType.BLADDER_2]: ['had_surgery_for_bladder_cancer', 'procedures', 'treatment_for_bladder_cancer', 'treatments'],
    [CancerType.RCC]: ['had_procedure_for_kidney_cancer', 'treatment_for_kidney_cancer', 'treatment_for_advanced_metastatic_disease', 'treatments'],
    [CancerType.GI_TRACT]: ['treatment_for_advanced_metastatic_disease', 'treatment_for_gi_tract', 'treatments'],
    [CancerType.MESO]: ['treatment_for_advanced_metastatic_disease', 'treatment_for_mesothelioma_cancer', 'treatments'],
    [CancerType.GENERAL]: ['treatment_for_advanced_metastatic_disease', 'treatment_for_cancer', 'treatments'],
    [CancerType.CML]: ['treatment_for_cml', 'treatments', 'received_transplant'],
    [CancerType.OVARIAN]: ['treatment_for_ovarian_cancer', 'treatment_for_advanced_metastatic_disease', 'treatments'],
    [CancerType.UTERINE]: ['treatment_for_uterine_cancer', 'treatments', 'had_procedure_for_uterine_cancer', 'procedures'],
    [CancerType.HNSCC]: ['treatment_for_hnscc', 'treatment_for_advanced_metastatic_disease', 'treatments', 'had_procedure', 'procedures'],
    [CancerType.BRAIN]: ['treatment_for_brain_cancer', 'treatments'],
    [CancerType.MDS]: ['treatment_for_mds', 'treatment_nontransplant_candidate', 'received_allogenic_transplant', 'allogenic_transplant_date', 'allogenic_transplant_outcome', 'treatment_after_transplant_relapse', 'treatment_after_transplant_relapse_outcome'],
    [CancerType.MPN]: ['treatment_for_mpn', 'treatments', 'received_allogenic_transplant'],
    [CancerType.MM]: ['received_transplant', 'treatment_for_mm', 'treatments'],
    [CancerType.NHL]: [
      'treatment_for_lymphoma',
      'treatments',
      'procedure_candidate',
      'had_procedure_for_lymphoma',
      'procedures',
    ],
    [CancerType.CLL]: ['treatment_for_cll', 'treatments', 'received_transplant'],
    [CancerType.AML]: ['treatment_for_aml', 'treatments'],
    [CancerType.HCC]: ['treatment_for_advanced_metastatic_disease', 'treatment_for_liver', 'treatments'],
    [CancerType.SARCOMA]: ['treatment_for_sarcoma', 'treatments', 'disease_measurable'],
    [CancerType.CERVICAL]: ['treatment_for_cervical_cancer', 'treatment_for_advanced_metastatic_disease', 'treatments'],
  },
  [INTAKE_SECTIONS.ABOUT_YOUR_HEALTH]: {
    [CancerType.BREAST_2]: ['ngs', 'biomarkers'],
    [CancerType.LUNG]: ['ngs', 'biomarkers', 'braf_mutation', 'egft_mut', 'erbb2_her2_mut', 'met_mut', 'kras_mut', 'pd_l1_carrier_status', 'pd_l1_expression'],
    [CancerType.CRC_2]: ['ngs', 'biomarkers', 'msi', 'kras_mut'],
    [CancerType.PROSTATE]: ['ngs', 'biomarkers'],
    [CancerType.MELANOMA_2]: ['ngs', 'biomarkers', 'braf_mutation'],
    [CancerType.BLADDER_2]: ['ngs', 'biomarkers'],
    [CancerType.RCC]: ['ngs', 'biomarkers'],
    [CancerType.GI_TRACT]: ['ngs', 'biomarkers'],
    [CancerType.MESO]: ['ngs', 'biomarkers'],
    [CancerType.GENERAL]: ['ngs', 'biomarkers', 'kras_mut'],
    [CancerType.CML]: ['ngs', 'biomarkers', 'bcr_abl_variants', 'bcr_abl_mutations'],
    [CancerType.OVARIAN]: ['ngs', 'biomarkers', 'msi'],
    [CancerType.UTERINE]: ['ngs', 'biomarkers', 'msi'],
    [CancerType.HNSCC]: ['ngs', 'biomarkers', 'pd_l1_carrier_status'],
    [CancerType.BRAIN]: ['ngs', 'biomarkers'],
    [CancerType.MDS]: ['ngs', 'biomarkers'],
    [CancerType.MPN]: ['ngs', 'biomarkers'],
    [CancerType.MM]: [],
    [CancerType.NHL]: ['ngs', 'biomarkers'],
    [CancerType.CLL]: ['ngs', 'biomarkers'],
    [CancerType.AML]: ['ngs', 'biomarkers'],
    [CancerType.HCC]: ['ngs', 'biomarkers'],
    [CancerType.SARCOMA]: ['ngs', 'biomarkers'],
    [CancerType.CERVICAL]: ['ngs', 'biomarkers'],
  },
  [INTAKE_SECTIONS.ABOUT_YOU]: {
    all: [
      'about',
      'name',
      ECOG_QUESTION_ID,
      INITIAL_DIAGNOSIS_DATE_QUESTION_ID,
      OTHER_MEDICAL_CONDITION_QUESTION_ID,
      'location',
      'have_insurance',
      'insurances',
    ],
  },
};

export const INTAKE_ABOUT_QUESTION = {
  id: 'about',
  type: QUESTION_TYPE.INTAKE_ABOUT_YOU,
  intakeType: INTAKE_SECTIONS.ABOUT_YOU,
  intakeTitle: 'intake.about_title',
  isQuestionAnswered: (user) => {
    if (!user?.info?.user_type) return false;
    if (!user?.personal?.dob) return false;
    if (hasGenderQuestion() && !user?.personal?.gender) return false;
    if (!user?.condition_profile.race?.length) return false;
    return true;
  },
  getQuestionValue: (user) => `${hasGenderQuestion() && user?.personal?.gender}, ${new Date(user.personal.dob).getFullYear()}, ${user.condition_profile.race.join(', ')}`,
  setQuestionValue: noop,
};

export const INTAKE_LOCATION_QUESTION = {
  id: 'location',
  type: QUESTION_TYPE.INTAKE_LOCATION,
  intakeType: INTAKE_SECTIONS.ABOUT_YOU,
  intakeTitle: 'Location preferences',
  isQuestionAnswered: (user) => !isEmptyObject(user?.info?.country),
  getQuestionValue: (user) => `${user?.info?.country?.label}${user?.personal?.travel_limit ? `, ${window.t(`questionnaire.questions.global.travel_limit.options.${user?.personal?.travel_limit}`)}` : ''}`,
  setQuestionValue: noop,
};

export const INTAKE_BIOMARKERS_QUESTION = {
  id: BIOMARKERS_QUESTION_ID,
  type: QUESTION_TYPE.BIOMARKERS,
  intakeType: 'general',
  intakeTitle: 'Location preferences',
  isQuestionAnswered: (user) => !isEmptyObject(user?.info?.country),
  getQuestionValue: (user) => `${user?.info?.country?.label}${user?.personal?.travel_limit ? `, ${window.t(`questionnaire.questions.global.travel_limit.options.${user?.personal?.travel_limit}`)}` : ''}`,
  setQuestionValue: noop,
};

export const INTAKE_NAME_QUESTION = {
  id: 'name',
  type: QUESTION_TYPE.TEXT_INPUT,
  intakeType: INTAKE_SECTIONS.ABOUT_YOU,
  intakeTitle: 'questionnaire.questions.global.full_name.title',
  title: 'questionnaire.questions.global.full_name.title',
  // showTitle: true,
  isQuestionAnswered: (user) => user?.info?.family_name,
  getQuestionValue: (user) => user?.info?.family_name,
  setQuestionValue: (newValue, user) => {
    // !!! user mutation
    // eslint-disable-next-line no-param-reassign
    user.info.family_name = newValue;
    // value on the user is set on the renderer
  },
};

export const CUSTOM_INTAKE_QUESTIONS = [
  INTAKE_ABOUT_QUESTION,
  INTAKE_NAME_QUESTION,
  INTAKE_LOCATION_QUESTION,
];

export const UPLOAD_INTAKE_QUESTIONS = [
  'type',
  'subtype',
  'biomarkers',
  'disease_status',
  'disease_location',
  'disease_stage',
  'received_treatment',
  'treatment_for_advanced_metastatic_disease',
  'treatments',
];

export const QUESTION_TITLE_TRANSLATION_KEY_BY_QUESTION_ID = {
  [BIOMARKERS_QUESTION_ID]: 'intake.question.biomarkers.title',
  [TREATMENTS_QUESTION_ID]: 'intake.question.treatments.title',
  [PROCEDURES_QUESTION_ID]: 'intake.question.procedures.title',
  [ECOG_QUESTION_ID]: 'intake.question.ecog.title',
  [INITIAL_DIAGNOSIS_DATE_QUESTION_ID]: 'intake.question.initial_diagnosis_date.title',
  [OTHER_MEDICAL_CONDITION_QUESTION_ID]: 'intake.question.other_medical_condition.title',
  [NGS_QUESTION_ID]: 'intake.question.ngs.title',
};
